/**
 * Non-destructive 16:9 media frame (PBI #1161).
 * Percentages match react-easy-crop `croppedAreaPercentages` over the source.
 */

/** Fixed media-slot aspect ratio for e-card artwork (matches CSS `[data-card-media]`). */
export const MEDIA_FRAME_ASPECT_RATIO = 16 / 9;

/** FormData / API field name — keep in sync with MediaFrameFormFields.MediaFrameJson. */
export const MEDIA_FRAME_FORM_FIELD = 'mediaFrameJson';

/**
 * Crop window as percentages of the source media (0–100).
 */
export interface MediaFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Type guard for a MediaFrame-shaped object from the API.
 */
export function isMediaFrame(value: unknown): value is MediaFrame {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  const v = value as Record<string, unknown>;

  // Reject pixel-sized values mistaken for percentages (e.g. 1920×1080 from
  // swapping onCropComplete args) — those make applyMediaFrameStyle shrink media
  // to a few percent of the viewport (blank gray preview).
  return (
    typeof v.x === 'number' &&
    typeof v.y === 'number' &&
    typeof v.width === 'number' &&
    typeof v.height === 'number' &&
    Number.isFinite(v.x) &&
    Number.isFinite(v.y) &&
    Number.isFinite(v.width) &&
    Number.isFinite(v.height) &&
    v.x >= 0 &&
    v.y >= 0 &&
    v.width > 0 &&
    v.height > 0 &&
    v.width <= 100 &&
    v.height <= 100
  );
}

/**
 * Resolves a MediaFrame from an API object and/or MediaFrameJson string
 * (CardTemplate / ScheduledCard entities expose the raw JSON column).
 */
export function resolveMediaFrame(source: {
  mediaFrame?: MediaFrame | null;
  mediaFrameJson?: string | null;
} | null | undefined): MediaFrame | null {
  if (!source) {
    return null;
  }

  if (isMediaFrame(source.mediaFrame)) {
    return source.mediaFrame;
  }

  if (typeof source.mediaFrameJson === 'string' && source.mediaFrameJson.trim()) {
    try {
      const parsed: unknown = JSON.parse(source.mediaFrameJson);
      return isMediaFrame(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Resolves the MediaFrame shown while scheduling / editing a card.
 *
 * Prefer a staged override file's frame; otherwise parse the template's
 * `mediaFrame` object or `mediaFrameJson` string from the API entity.
 * Reading only `.mediaFrame` misses catalog rows that only have MediaFrameJson.
 */
export function resolveEffectiveMediaFrame(options: {
  pendingFile?: File | null;
  pendingFrame?: MediaFrame | null;
  template?: {
    mediaFrame?: MediaFrame | null;
    mediaFrameJson?: string | null;
  } | null;
}): MediaFrame | null {
  if (options.pendingFile) {
    return options.pendingFrame ?? null;
  }

  return resolveMediaFrame(options.template);
}

/** Entity shape that may carry MediaFrame as object and/or MediaFrameJson string. */
export type MediaFrameSource = {
  mediaFrame?: MediaFrame | null;
  mediaFrameJson?: string | null;
};

/**
 * Copies a resolved MediaFrame onto `mediaFrame` so UI can rely on the object
 * after a service fetch. Call this at every API boundary that returns templates
 * or scheduled cards (PBI #1161). Does not remove mediaFrameJson.
 */
export function hydrateMediaFrameFields<T extends MediaFrameSource>(entity: T): T {
  return { ...entity, mediaFrame: resolveMediaFrame(entity) };
}

/**
 * Hydrates MediaFrame on each template in a catalog page response.
 */
export function hydrateCardTemplateCatalogPage<T extends { templates: MediaFrameSource[] }>(
  page: T,
): T {
  return {
    ...page,
    templates: page.templates.map((t) => hydrateMediaFrameFields(t)),
  };
}
